// S9 Floor lookup (floor-pwa area, /floor/lookup). Scan a product-card QR → a
// read-only card: SKU, name, attributes, and the active routing steps for the
// worker's facility with instructions. Offline-first: products + routings are
// served from the PWA's synced floor cache (delta sync with tombstones), and a
// banner shows the cache age (§4 S9, §9.14). No cost, no BOM quantities (§4/§7):
// the floor gets per-WO material lists from module 04, and the floor-cache feed
// strips cost fields unconditionally.
//
// Facility: a floor station is provisioned once with its facility (via a
// `?facility=<id>` deep link or the on-screen setup), remembered in localStorage
// so it survives reloads and offline. Scan value: a keyboard-wedge scanner types
// the decoded QR URL into the focused field; `parseScanValue` resolves the
// product id from the URL, the `/q/p/{uuid}` path, or a bare uuid.
//
// PROVISIONING PICKS A NAME, LIKE EVERY OTHER FLOOR SURFACE (UX-5 surface
// review). This screen used to ask an operator to TYPE A FACILITY UUID into an
// unvalidated free-text field — placeholder "Facility ID", saved verbatim on
// any non-empty string, with `/v1/org/facilities` sitting right there and every
// sibling (the station picker, the wall board) already choosing a facility BY
// NAME from it. A mistyped uuid is not refused here: it is persisted to
// localStorage, and the screen then syncs an empty catalogue and reports
// "Product not found" for every scan on the floor, forever, with nothing
// naming the cause. There is no operator anywhere who knows a uuid by heart, so
// the field could only ever be filled by copying one out of the office — which
// is a provisioning step, not a shopfloor one. The deep link still carries an
// id (that IS the provisioning path), the saved id still survives offline, and
// what the glass offers is the list.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { client } from "../../../lib/api";
import { RequestError, unwrap } from "../../../lib/request";
import { EmptyState } from "../../../components/states";
import { useFloorCatalogue } from "../hooks/use-floor-catalogue";
import { findProduct, findRouting, loadFacilityId, saveFacilityId } from "../lib/floor-cache-store";
import { parseScanValue } from "../lib/scan";
import { CacheBanner } from "../components/cache-banner";
import { ProductCard } from "../components/product-card";
import { FloorEmpty, FloorError, FloorSkeleton } from "../components/floor-chrome";
import { useFacilities } from "../../office/production/hooks/production-queries";
import "./floor-lookup.css";

function readSearch(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

function CardSkeleton() {
  return (
    <div className="state-skeleton" data-testid="card-skeleton" aria-hidden="true">
      <span className="skeleton" style={{ height: "1.6rem", width: "60%" }} />
      <span className="skeleton" style={{ width: "40%" }} />
      <span className="skeleton" />
      <span className="skeleton" />
      <span className="skeleton" style={{ width: "80%" }} />
    </div>
  );
}

export function FloorLookupScreen() {
  const { t } = useTranslation();

  const [facilityId, setFacilityId] = useState<string | undefined>(() => {
    const fromUrl = readSearch().get("facility");
    return fromUrl !== null && fromUrl.length > 0 ? fromUrl : loadFacilityId();
  });
  const [configuring, setConfiguring] = useState<boolean>(false);

  const [inputValue, setInputValue] = useState<string>(() => {
    const params = readSearch();
    return params.get("product") ?? params.get("p") ?? "";
  });
  const [productId, setProductId] = useState<string | undefined>(() => {
    const params = readSearch();
    const raw = params.get("product") ?? params.get("p") ?? "";
    return raw.length > 0 ? parseScanValue(raw) : undefined;
  });
  const [scanUnreadable, setScanUnreadable] = useState<boolean>(false);

  // Remember the provisioned facility across reloads / offline.
  useEffect(() => {
    if (facilityId !== undefined) saveFacilityId(facilityId);
  }, [facilityId]);

  // Fetched only while the picker is on screen: this is a provision-once
  // control, and a floor tablet that is already provisioned has no business
  // polling the org tree on every lookup.
  const facilityList = useFacilities(facilityId === undefined || configuring);

  const cat = useFloorCatalogue(facilityId);
  const online = !cat.isOffline;

  const cachedProduct = productId !== undefined ? findProduct(cat.snapshot, productId) : undefined;

  // Online fallback: a freshly-activated product not yet in the delta still
  // resolves via the product card endpoint (catalogue.read — the floor role has
  // it). Routing is never fetched here: floor roles lack `catalogue.structure.read`,
  // so routing steps come only from the floor cache.
  const onlineProduct = useQuery({
    queryKey: ["floor", "product", productId ?? null],
    enabled:
      productId !== undefined &&
      cachedProduct === undefined &&
      online &&
      !cat.isInitialLoading &&
      facilityId !== undefined,
    retry: false,
    networkMode: "always",
    queryFn: async ({ signal }) => {
      if (productId === undefined) throw new Error("product required");
      return unwrap(
        await client.GET("/v1/catalogue/products/{id}", {
          signal,
          params: { path: { id: productId } },
        }),
        "product",
      );
    },
  });

  const product = cachedProduct ?? onlineProduct.data;
  const routing = productId !== undefined ? findRouting(cat.snapshot, productId) : undefined;

  function submitScan() {
    const id = parseScanValue(inputValue);
    if (id === undefined) {
      setScanUnreadable(inputValue.trim().length > 0);
      setProductId(undefined);
      return;
    }
    setScanUnreadable(false);
    setProductId(id);
  }

  function clearScan() {
    setInputValue("");
    setProductId(undefined);
    setScanUnreadable(false);
  }

  // ---- Facility provisioning (blocks the screen until set) ------------------

  if (facilityId === undefined || configuring) {
    const items = facilityList.data?.items ?? [];
    return (
      <section className="floor-lookup floor-config" data-testid="floor-facility-config">
        <h1>{t("catalogue.screens.floorLookup")}</h1>
        <div className="floor-config-card">
          <p className="floor-config-title">{t("catalogue.floorLookup.facilityPromptTitle")}</p>
          <p className="floor-config-body">{t("catalogue.floorLookup.facilityPromptBody")}</p>

          {facilityList.isPending ? <FloorSkeleton rows={3} /> : null}

          {/* The list is the ONLY way in, so a list that did not arrive is a
              state this screen has to state — not an empty page with a dead
              form on it. Offline-and-never-provisioned genuinely cannot be
              resolved on the glass (the names live on the server), and saying
              so is the honest answer; the deep link and the already-saved id
              are the two paths that do not need it. */}
          {facilityList.error !== null ? (
            <FloorError
              title={t("catalogue.floorLookup.facilityErrorTitle")}
              detail={t("catalogue.floorLookup.facilityErrorBody")}
              requestId={
                facilityList.error instanceof RequestError
                  ? facilityList.error.requestId
                  : undefined
              }
              onRetry={() => {
                void facilityList.refetch();
              }}
            />
          ) : null}

          {!facilityList.isPending && facilityList.error === null && items.length === 0 ? (
            <FloorEmpty
              title={t("catalogue.floorLookup.facilityEmptyTitle")}
              body={t("catalogue.floorLookup.facilityEmptyBody")}
            />
          ) : null}

          {items.length > 0 ? (
            <ul className="floor-cards" data-testid="floor-facility-list">
              {items.map((facility) => (
                <li key={facility.id} className="floor-card">
                  <p className="floor-card-title">{facility.name}</p>
                  <p className="floor-card-sub">{facility.code}</p>
                  {/* THE FLOOR'S BUTTON, not the office's. The control this
                      replaced wore `.btn-primary` — the pre-register office
                      accent button (29 px tall, 12.5 px label, office.css's
                      documented compatibility class with 46 call sites, all of
                      them in the office) — and rendered as an office control
                      stretched to 52 px by `.floor-area button`'s glove
                      backstop with an office-sized label inside it. The
                      design-system gate cannot see that: check 3b patrols LINKS
                      wearing button classes, and this is a real `<button>`. */}
                  <button
                    type="button"
                    className="floor-btn floor-btn-primary"
                    data-testid="floor-facility-pick"
                    onClick={() => {
                      setFacilityId(facility.id);
                      setConfiguring(false);
                    }}
                  >
                    {t("catalogue.floorLookup.facilityPick")}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {/* Only when there is something to go back TO. Tapping "Change
              facility" used to be a one-way door: the form had no exit and the
              screen is blocked until something is saved, so a mis-tap cost the
              operator their provisioned facility unless they could retype the
              uuid. */}
          {configuring && facilityId !== undefined ? (
            <button
              type="button"
              className="floor-btn floor-btn-ghost"
              data-testid="floor-facility-cancel"
              onClick={() => {
                setConfiguring(false);
              }}
            >
              {t("catalogue.floorLookup.facilityCancel")}
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  // ---- Result region --------------------------------------------------------

  function renderResult() {
    if (productId === undefined) {
      return (
        <div className="floor-prompt" data-testid="floor-scan-prompt">
          <p className="floor-prompt-line">{t("catalogue.floorLookup.scanPrompt")}</p>
          <p className="floor-prompt-hint">{t("catalogue.floorLookup.scanHint")}</p>
          {scanUnreadable ? (
            <p className="floor-scan-error" role="alert">
              {t("catalogue.floorLookup.scanUnreadable")}
            </p>
          ) : null}
        </div>
      );
    }

    if (product !== undefined) {
      return <ProductCard product={product} routing={routing} />;
    }

    if (cat.isInitialLoading || onlineProduct.isFetching) {
      return <CardSkeleton />;
    }

    // Offline and never synced: nothing to look up in.
    if (cat.isOffline && cat.snapshot === undefined) {
      return (
        <EmptyState
          title={t("catalogue.floorLookup.cacheEmptyTitle")}
          body={t("catalogue.floorLookup.cacheEmptyBody")}
          ctaLabel={t("catalogue.floorLookup.refresh")}
          onCta={cat.refresh}
        />
      );
    }

    // A real transport failure (not a 404) → error card with the request id.
    const err = onlineProduct.error instanceof RequestError ? onlineProduct.error : undefined;
    if (err !== undefined && err.httpStatus !== 404) {
      return (
        <div className="state state-error" role="alert" data-testid="floor-error">
          <p className="state-title">{t("catalogue.floorLookup.syncErrorTitle")}</p>
          <p className="state-detail">{t("catalogue.floorLookup.syncErrorBody")}</p>
          {err.requestId !== undefined ? (
            <p className="state-request-id">
              {t("catalogue.state.requestId")}: <code>{err.requestId}</code>
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => {
              void onlineProduct.refetch();
            }}
          >
            {t("catalogue.state.retry")}
          </button>
        </div>
      );
    }

    // Not found: unknown/foreign/discontinued id (online 404) or simply absent
    // from the offline cache. No cross-tenant probe signal either way (§3.1).
    return (
      <div className="floor-notfound" role="status" data-testid="floor-not-found">
        <p className="floor-notfound-title">{t("catalogue.floorLookup.notFoundTitle")}</p>
        <p className="floor-notfound-body">
          {cat.isOffline
            ? t("catalogue.floorLookup.notFoundOfflineBody")
            : t("catalogue.floorLookup.notFoundBody")}
        </p>
        <button type="button" onClick={clearScan}>
          {t("catalogue.floorLookup.clear")}
        </button>
      </div>
    );
  }

  return (
    <section className="floor-lookup" data-testid="floor-lookup">
      <div className="floor-lookup-head">
        <h1>{t("catalogue.screens.floorLookup")}</h1>
        <button
          type="button"
          className="floor-change-facility"
          // Nothing to seed any more: the picker is a LIST of facilities
          // fetched while it is open, not a text field that had to be
          // pre-filled with the current uuid so the operator could edit it.
          // `setFacilityInput` went with the field it belonged to.
          onClick={() => {
            setConfiguring(true);
          }}
        >
          {t("catalogue.floorLookup.changeFacility")}
        </button>
      </div>

      <CacheBanner
        online={online}
        stale={cat.isStale}
        syncing={cat.isSyncing}
        syncedAt={cat.syncedAt}
        onRefresh={cat.refresh}
      />

      <form
        className="floor-scan"
        onSubmit={(e) => {
          e.preventDefault();
          submitScan();
        }}
      >
        <label htmlFor="floor-scan-input">{t("catalogue.floorLookup.scanLabel")}</label>
        <div className="floor-scan-row">
          <input
            id="floor-scan-input"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoFocus
            value={inputValue}
            placeholder={t("catalogue.floorLookup.scanPlaceholder")}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (scanUnreadable) setScanUnreadable(false);
            }}
          />
          {/* Same leak as the provisioning form above: the office accent button
              on a floor screen. The bare `<button>` beside it had no class at
              all, so it was whatever `base.css` gives an element — the floor's
              scan row is the one place an operator uses two controls side by
              side and they must read as the same pair. */}
          <button type="submit" className="floor-btn floor-btn-primary floor-scaninput-go">
            {t("catalogue.floorLookup.lookup")}
          </button>
          {inputValue.length > 0 || productId !== undefined ? (
            <button type="button" className="floor-btn floor-scaninput-go" onClick={clearScan}>
              {t("catalogue.floorLookup.clear")}
            </button>
          ) : null}
        </div>
      </form>

      <div className="floor-result">{renderResult()}</div>
    </section>
  );
}
